import { computed, FunctionPlugin, inject } from 'vue'
import { useI18nAddon } from '@/i18n/i18n'
import { AvailableLocale } from '@/i18n/types'
import { RouteLocationNamedRaw, Router, useRoute } from 'vue-router'
import { useConfig } from './config'
import { ROUTE_NAMES } from '@/router/constants'
import { mapLocaleToHrefLang } from '@/i18n/helpers'

const LOCALIZED_ROUTERS_PROVIDE_KEY = Symbol('localized-routers')

export const createConfigLocalizedRouters =
    (routers: Record<AvailableLocale, Router>): FunctionPlugin =>
    (app) => {
        app.provide(LOCALIZED_ROUTERS_PROVIDE_KEY, routers)
    }

type MetaHrefLink = {
    rel: string
    href: string
    hreflang: string
    'data-lang-switch': string
    key: string
    'data-locale': string
}

const removeRouteQuery = (x: RouteLocationNamedRaw): RouteLocationNamedRaw => ({ ...x, query: {} })

export function useLocalizedRouters() {
    const defaultLocale = useI18nAddon().getDefaultLocale()

    const routers = inject<Record<AvailableLocale, Router>>(LOCALIZED_ROUTERS_PROVIDE_KEY)
    const { VUE_APP_HOST_URL } = useConfig()

    if (!routers) {
        throw new Error('No localized routers provided in Vue app.')
    }

    const currentRoute = useRoute()
    // turn RouteLocationNormalizedLoaded into RouteLocationNamedRaw
    const normalizedCurrentRoute = computed(
        (): RouteLocationNamedRaw =>
            currentRoute.name
                ? {
                      name: currentRoute.name || undefined,
                      params: currentRoute.params,
                      query: currentRoute.query,
                  }
                : {
                      name: ROUTE_NAMES.MAIN,
                  },
    )

    const getHrefLangs = ({
        forRoute = normalizedCurrentRoute.value,
        mapRoute = removeRouteQuery,
    }: {
        forRoute?: RouteLocationNamedRaw
        mapRoute?: (route: RouteLocationNamedRaw, locale: AvailableLocale) => RouteLocationNamedRaw
    } = {}): MetaHrefLink[] => {
        const result: MetaHrefLink[] = []
        const currentLocale = normalizedCurrentRoute.value.params?.locale || defaultLocale

        ;(Object.entries(routers) as Array<[AvailableLocale, Router]>).forEach(([locale, localizedRouter]) => {
            const { locale: _, ...forRouteParams } = forRoute.params || {}
            const { locale: __, ...currentRouteParams } = normalizedCurrentRoute.value.params || {}
            const forRouteMapped = mapRoute({ ...forRoute, params: forRouteParams }, locale)
            const forCurrentRouteMapped = mapRoute(
                {
                    ...normalizedCurrentRoute.value,
                    params: currentRouteParams,
                },
                locale,
            )
            const switchLink = { ...forCurrentRouteMapped, query: normalizedCurrentRoute.value.query }

            result.push({
                rel: locale === currentLocale ? 'canonical' : 'alternate',
                href: `${VUE_APP_HOST_URL}${localizedRouter.resolve(forRouteMapped).fullPath}`,
                'data-lang-switch': localizedRouter.resolve(switchLink).fullPath,
                'data-locale': locale,
                hreflang: mapLocaleToHrefLang(locale),
                key: `hreflang-${locale}`,
            })
            if (locale === defaultLocale) {
                result.push({
                    rel: 'alternate',
                    href: `${VUE_APP_HOST_URL}${localizedRouter.resolve(forRouteMapped).fullPath}`,
                    'data-lang-switch': localizedRouter.resolve(switchLink).fullPath,
                    hreflang: 'x-default',
                    'data-locale': locale,
                    key: 'hreflang-default',
                })
            }
        })
        return result
    }

    return { getHrefLangs }
}
